import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, sessionParam } from "../utils/adminSession";

const API = window.location.pathname.includes("/finaloutput/public_html")
  ? "/finaloutput/public_html/api"
  : "/api";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!match) return "";

  return `${match[3]}-${match[2]}-${match[1]}`;
};
const uploadsBase = "/uploads";

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 3000 };
const modalStyle = { background: "#fff", width: "min(1100px, 100%)", maxHeight: "92vh", overflow: "auto", borderRadius: "22px", padding: "24px", boxShadow: "0 30px 70px rgba(15,23,42,0.28)" };
const detailGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" };
const tableShellStyle = { overflowX: "auto", overflowY: "visible", background: "#fff", boxShadow: "0 .125rem .25rem rgba(0,0,0,.075)", borderRadius: "1rem", padding: "0.5rem", position: "relative", zIndex: 1 };
const popupMenuStyle = { position: "fixed", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", boxShadow: "0 18px 30px rgba(15,23,42,0.18)", padding: "8px", display: "grid", gap: "6px", zIndex: 5000, minWidth: "210px" };
const toastStyle = { position: "fixed", top: "84px", right: "20px", minWidth: "280px", maxWidth: "360px", padding: "14px 16px", borderRadius: "16px", boxShadow: "0 18px 40px rgba(15,23,42,0.18)", zIndex: 6000, display: "grid", gap: "4px" };

function fileUrl(name) {
  return name ? `${uploadsBase}/${name}` : "";
}

function StatusChip({ status }) {
  const current = status || "pending";
  const styles = {
    approved: { color: "#166534", background: "#dcfce7" },
    hold: { color: "#92400e", background: "#fef3c7" },
    rejected: { color: "#b91c1c", background: "#fee2e2" },
    pending: { color: "#1d4ed8", background: "#dbeafe" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        ...(styles[current] || styles.pending),
      }}
    >
      {current}
    </span>
  );
}

function DetailTile({ label, value }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "14px" }}>
      <div style={{ color: "#64748b", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  );
}

function DocumentCard({ label, fileName, isImage }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "14px", textAlign: "center" }}>
      <div style={{ color: "#475569", fontWeight: 700, marginBottom: "10px" }}>{label}</div>
      {fileName ? (
        isImage ? (
          <img src={fileUrl(fileName)} alt={label} style={{ width: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "12px" }} />
        ) : (
          <a href={fileUrl(fileName)} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
            Open {label}
          </a>
        )
      ) : (
        <div style={{ color: "#94a3b8" }}>Not uploaded</div>
      )}
    </div>
  );
}

function ViewStudentApplications() {
  let currentAdmin = null;
  try {
    currentAdmin = JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    currentAdmin = null;
  }

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showView, setShowView] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFiles, setEditFiles] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [statusPopup, setStatusPopup] = useState(null);
  const [statusRemark, setStatusRemark] = useState("");
  const [toast, setToast] = useState(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student-applications`, {
        params: { workflow_stage: "admin_review", session_year: sessionParam(), _ts: Date.now() },
      });
      setRows(res.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    const reload = () => loadRows();
    window.addEventListener(ADMIN_SESSION_EVENT, reload);
    return () => window.removeEventListener(ADMIN_SESSION_EVENT, reload);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;

    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
  };

  const openView = async (id) => {
    try {
      const res = await axios.get(`${API}/student-applications/${id}`);
      setSelected(res.data);
      setShowView(true);
    } catch (error) {
      window.alert(error.response?.data?.message || "Student data load failed");
    }
  };

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/student-applications/${id}`);
      const item = res.data;
      setEditingItem(item);
      setEditForm({
        student_name: item.student_name || "",
        phone: item.phone || "",
        email: item.email || "",
        dob: toDateInputValue(item.dob),
        father_name: item.father_name || "",
        admission_date: toDateInputValue(item.admission_date),
        guardian_name: item.guardian_name || "",
        address: item.address || "",
        course_id: item.course_id || "",
      });
      setEditFiles({});
    } catch (error) {
      window.alert(error.response?.data?.message || "Edit data load failed");
    }
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFile = (event) => {
    const { name, files } = event.target;
    setEditFiles((prev) => ({ ...prev, [name]: files?.[0] || null }));
  };

  const saveEdit = async () => {
    if (!editingItem?.id) return;

    try {
      setSavingEdit(true);
      const payload = new FormData();
      Object.entries(editForm).forEach(([key, value]) => payload.append(key, value || ""));
      Object.entries(editFiles).forEach(([key, value]) => {
        if (value) payload.append(key, value);
      });
      await axios.post(`${API}/student-applications/${editingItem.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingItem(null);
      await loadRows();
      showToast("success", "Student Updated", "Student application details were updated successfully.");
    } catch (error) {
      showToast("error", "Update Failed", error.response?.data?.message || "Student update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const updateStatus = async (id, status) => {
    if ((status === "hold" || status === "rejected") && !statusRemark.trim()) {
      showToast("error", "Remark Required", "Hold or reject করার জন্য remark লিখতে হবে.");
      return;
    }

    try {
      await axios.post(`${API}/student-applications/${id}/status`, {
        status,
        remark: statusRemark.trim(),
        admin_id: currentAdmin?.login_id || "master_admin",
      });
      setStatusPopup(null);
      setStatusRemark("");
      await loadRows();
      showToast("success", "Status Updated", `Student application moved to ${status}.`);
    } catch (error) {
      showToast("error", "Status Failed", error.response?.data?.message || "Status update failed");
    }
  };

  const actionButtonStyle = (variant) => ({
    border: "none",
    borderRadius: "10px",
    padding: "8px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    background:
      variant === "green"
        ? "#dcfce7"
        : variant === "yellow"
        ? "#fef3c7"
        : variant === "red"
        ? "#fee2e2"
        : variant === "blue"
        ? "#dbeafe"
        : "#e2e8f0",
    color:
      variant === "green"
        ? "#166534"
        : variant === "yellow"
        ? "#92400e"
        : variant === "red"
        ? "#b91c1c"
        : variant === "blue"
        ? "#1d4ed8"
        : "#0f172a",
  });

  const statusMenuButtonStyle = (variant) => ({
    ...actionButtonStyle(variant),
    width: "100%",
    height: "auto",
    justifyContent: "flex-start",
    padding: "8px 10px",
    gap: "8px",
  });

  const openStatusPopup = (event, id) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 210;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12));
    const top = Math.max(12, rect.top - 150);

    setStatusPopup((prev) => (prev?.id === id ? null : { id, top, left }));
    setStatusRemark("");
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h3 className="mb-1">Student View Application</h3>
            <p className="text-muted mb-0">Session: {getSelectedAdminSession()} | Students sent by franchise for registration approval are listed here.</p>
          </div>
          <button className="btn btn-dark" onClick={loadRows} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={tableShellStyle}>
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Registration No</th>
                <th>Student</th>
                <th>Study Centre Name</th>
                <th>State</th>
                <th>District</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>{row.registration_no || "-"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={row.photo ? fileUrl(row.photo) : "https://placehold.co/52x52/e2e8f0/475569?text=ST"}
                          alt={row.student_name}
                          style={{ width: "52px", height: "52px", borderRadius: "14px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                        />
                        <div>
                          <strong>{row.student_name}</strong>
                          <div className="text-muted small">{row.application_no || `APP-${row.id}`}</div>
                        </div>
                      </div>
                    </td>
                    <td>{row.study_centre_name || row.study_centre_login || "-"}</td>
                    <td>{row.state_name || "-"}</td>
                    <td>{row.district_name || "-"}</td>
                    <td>
                      <div style={{ display: "grid", gap: "6px" }}>
                        <StatusChip status={row.status} />
                        {row.status_remark ? <div style={{ fontSize: "12px", color: "#64748b", lineHeight: 1.5 }}>Remark: {row.status_remark}</div> : null}
                        {row.status_action_by ? <div style={{ fontSize: "12px", color: "#475569", fontWeight: 700 }}>By: {row.status_action_by}</div> : null}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2 position-relative">
                        <button type="button" title="View" aria-label="View" style={actionButtonStyle("blue")} onClick={() => openView(row.id)}>
                          <i className="bi bi-eye"></i>
                        </button>
                        <button type="button" title="Edit" aria-label="Edit" style={actionButtonStyle("gray")} onClick={() => openEdit(row.id)}>
                          <i className="bi bi-pen"></i>
                        </button>
                        <button type="button" title="Status Action" aria-label="Status Action" style={actionButtonStyle("yellow")} onClick={(event) => openStatusPopup(event, row.id)}>
                          <i className="bi bi-ui-checks-grid"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No student applications found.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    Loading student applications...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {statusPopup ? (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 4990 }} onClick={() => setStatusPopup(null)} />
          <div style={{ ...popupMenuStyle, top: `${statusPopup.top}px`, left: `${statusPopup.left}px` }}>
            <div style={{ padding: "4px 2px 8px" }}>
              <div style={{ color: "#475569", fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>Remark</div>
              <textarea
                className="form-control"
                rows="3"
                value={statusRemark}
                onChange={(event) => setStatusRemark(event.target.value)}
                placeholder="Hold / reject er jonno remark likhun"
                style={{ minWidth: "194px", fontSize: "13px" }}
              ></textarea>
            </div>
            <button type="button" style={statusMenuButtonStyle("green")} onClick={() => updateStatus(statusPopup.id, "approved")}>
              <i className="bi bi-check-circle"></i>
              Approve
            </button>
            <button type="button" style={statusMenuButtonStyle("yellow")} onClick={() => updateStatus(statusPopup.id, "hold")}>
              <i className="bi bi-pause-circle"></i>
              Hold
            </button>
            <button type="button" style={statusMenuButtonStyle("red")} onClick={() => updateStatus(statusPopup.id, "rejected")}>
              <i className="bi bi-x-circle"></i>
              Reject
            </button>
          </div>
        </>
      ) : null}

      {toast ? (
        <div
          style={{
            ...toastStyle,
            background: toast.type === "success" ? "#ecfdf5" : "#fef2f2",
            border: `1px solid ${toast.type === "success" ? "#a7f3d0" : "#fecaca"}`,
            color: toast.type === "success" ? "#065f46" : "#991b1b",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 800 }}>
            <i className={`bi ${toast.type === "success" ? "bi-check2-circle" : "bi-exclamation-octagon"}`}></i>
            {toast.title}
          </div>
          <div style={{ fontSize: "13px", lineHeight: 1.5 }}>{toast.message}</div>
        </div>
      ) : null}

      {showView && selected ? (
        <div style={overlayStyle} onClick={() => setShowView(false)}>
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-1">Student Application View</h3>
                <p className="text-muted mb-0">Full application format including photo, signature, and uploaded documents.</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowView(false)}>
                Close
              </button>
            </div>

            <div style={detailGridStyle}>
              <DetailTile label="Application No" value={selected.application_no} />
              <DetailTile label="Registration No" value={selected.registration_no} />
              <DetailTile label="Student Name" value={selected.student_name} />
              <DetailTile label="Study Centre" value={selected.study_centre_name} />
              <DetailTile label="State" value={selected.state_name} />
              <DetailTile label="District" value={selected.district_name} />
              <DetailTile label="Phone" value={selected.phone} />
              <DetailTile label="Email" value={selected.email} />
              <DetailTile label="DOB" value={selected.dob} />
              <DetailTile label="Father Name" value={selected.father_name} />
              <DetailTile label="Admission Date" value={selected.admission_date} />
              <DetailTile label="Guardian Name" value={selected.guardian_name} />
              <DetailTile label="Session" value={selected.session_year} />
              <DetailTile label="Course" value={selected.course_name} />
              <DetailTile label="Status" value={selected.status} />
            </div>

            <div className="mt-3 p-3 rounded-4 border bg-light">
              <div className="text-muted small fw-bold mb-2">Address</div>
              <div>{selected.address || "-"}</div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6"><DocumentCard label="Photo" fileName={selected.photo} isImage /></div>
              <div className="col-md-6"><DocumentCard label="Signature" fileName={selected.signature} isImage /></div>
              <div className="col-md-6"><DocumentCard label="ID Proof" fileName={selected.id_proof} /></div>
              <div className="col-md-6"><DocumentCard label="Academic Document" fileName={selected.academic_document} /></div>
            </div>
          </div>
        </div>
      ) : null}

      {editingItem ? (
        <div style={overlayStyle} onClick={() => setEditingItem(null)}>
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-1">Edit Student Application</h3>
                <p className="text-muted mb-0">Main admin can correct full student data from here.</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingItem(null)}>
                Close
              </button>
            </div>

            <div className="row g-3">
              <div className="col-md-6"><label className="form-label">Student Name</label><input className="form-control" name="student_name" value={editForm.student_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Phone</label><input className="form-control" name="phone" value={editForm.phone || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Email</label><input className="form-control" name="email" value={editForm.email || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">DOB</label><input type="date" className="form-control" name="dob" value={editForm.dob || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Father Name</label><input className="form-control" name="father_name" value={editForm.father_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Admission Date</label><input type="date" className="form-control" name="admission_date" value={editForm.admission_date || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Guardian Name</label><input className="form-control" name="guardian_name" value={editForm.guardian_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Course ID</label><input className="form-control" name="course_id" value={editForm.course_id || ""} onChange={handleEditChange} /></div>
              <div className="col-12"><label className="form-label">Address</label><textarea className="form-control" rows="4" name="address" value={editForm.address || ""} onChange={handleEditChange}></textarea></div>
              {["photo", "signature", "id_proof", "academic_document"].map((field) => (
                <div className="col-md-6" key={field}>
                  <label className="form-label text-capitalize">{field.replaceAll("_", " ")}</label>
                  <input className="form-control" type="file" name={field} onChange={handleEditFile} />
                </div>
              ))}
            </div>

            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? "Saving..." : "Save Edit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </DashboardLayout>
  );
}

export default ViewStudentApplications;
